import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider.js";
import { AppRouter } from "./routes/router.js";
import { LoadingSplash } from "./components/shell/LoadingSplash.js";
import { ConnectionBanner } from "./components/shell/ConnectionBanner.js";
import { ImpersonationBanner } from "./components/admin/ImpersonationBanner.js";
import { useServerTheme } from "./preferences/useServerTheme.js";
import { LanguageProvider, ThemeProvider } from "@mmm/ui";

/**
 * Supplies the signed-in user's stored theme to the shared provider.
 *
 * Stays outermost, above the language gate, so `data-theme` is applied on the
 * very first frame — the API layer holds no React context, so reading
 * preferences here does not require the auth tree above it.
 */
function ServerThemeProvider({ children }: { children: ReactNode }) {
  return <ThemeProvider persistence={useServerTheme()}>{children}</ThemeProvider>;
}

export default function App() {
  return (
    <ServerThemeProvider>
      {/* Everything below renders translated text, so nothing mounts until
          the initial locale catalog has loaded (LanguageProvider gates). */}
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            {/* Route-aware: the authenticated workspace's loading treatment
                must never hide a screen that owns its own readiness signal. */}
            <ConnectionBanner />
            {/* App-level so it survives every route change — see the component
                for why an impersonation session must never be dismissible. */}
            <ImpersonationBanner />
            <LoadingSplash />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ServerThemeProvider>
  );
}
